import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useTranslation } from "react-i18next";
import CabiBubble from "../../components/Kids/CabiBubble";
import CabiMascot from "../../components/Kids/CabiMascot";
import Button from "../../components/Kids/Button";
import { track } from "../../utils/analytics";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

const API = process.env.REACT_APP_API_URL;

const STEP_INFO = [
  { mood: "waving",      tip: "Hi there! Let's set up your parent account. It only takes 2 minutes! 🚀" },
  { mood: "celebrating", tip: "Almost there! Enter the code we just emailed you! 🌟" },
];

export default function ParentSignup() {
  const { t }     = useTranslation();
  const navigate  = useNavigate();
  const [params]  = useSearchParams();
  const planSlug  = params.get("plan")    || "kids-starter";
  const billing   = params.get("billing") || "monthly";
  const promoFromUrl = params.get("promo") || "";

  const [step,      setStep]      = useState(1);
  const [loading,   setLoading]   = useState(false);
  const [resending, setResending] = useState(false);
  const [otp,        setOtp]        = useState("");
  const [verifying,  setVerifying]  = useState(false);
  const [otpError,   setOtpError]   = useState("");

  const [form, setForm] = useState({
    name: "", email: "", password: "", password_confirmation: "",
    phone: "", country: "", city: "", school: "", promo_code: promoFromUrl,
    plan_slug: planSlug, billing_cycle: billing,
  });

  const [errors, setErrors] = useState({});

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors({});

    // Client-side guards (backend enforces the same rules — this is just UX)
    const nameErr   = !/^[\p{L}\s-]{2,80}$/u.test(form.name.trim());
    const phoneDigits = form.phone.replace(/\D/g, "");
    const phoneErr  = phoneDigits.length < 10;
    if (nameErr || phoneErr) {
      setErrors({
        ...(nameErr  ? { name:  ["Name may only contain letters, spaces, and hyphens (min 2 characters)."] } : {}),
        ...(phoneErr ? { phone: ["Please enter a valid phone number with country code (min 10 digits)."] }  : {}),
      });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/kids/parent/register`, form);
      toast.success(
        res.data?.data?.promo_applied
          ? "Promo code applied! 🎉 Check your email for a verification code."
          : "Account created! Check your email for a code."
      );
      track("sign_up", { method: "email", product: "cabil_kids" });
      setStep(2);
    } catch (err) {
      setErrors(err.response?.data?.errors || {});
      toast.error(err.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/api/kids/parent/resend-verification`, { email: form.email });
      toast.success("New code sent — check your inbox (and spam folder)!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't resend the code. Try again shortly.");
    } finally {
      setResending(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setOtpError("");
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/api/kids/parent/verify-otp`, { email: form.email, otp });
      const { token, user, parent } = res.data.data;

      localStorage.setItem("kids_parent_token", token);
      localStorage.setItem("kids_parent_user", JSON.stringify(user));
      localStorage.setItem("kids_active_mode", "parent");
      const children = (parent?.children || []).map(c => ({
        id: c.id, name: c.name, avatar: c.avatar, has_pin: c.has_pin,
      }));
      localStorage.setItem("kids_family", JSON.stringify({ email: form.email, children }));

      track("generate_lead", { lead_source: "kids_signup_verified" });
      toast.success("Email verified! Welcome to Cabil Kids 🎉");
      navigate("/kids/parent/dashboard");
    } catch (err) {
      setOtpError(err.response?.data?.message || "That code is invalid or has expired.");
    } finally {
      setVerifying(false);
    }
  };

  const info = STEP_INFO[step - 1];

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 font-body"
      style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}
    >
      <Seo
        {...SEO["/kids/signup"]}
        path="/kids/signup"
      />
      <style>{`
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
        .slide-up { animation: slideUp 0.4s ease both; }
      `}</style>

      {/* Logo */}
      <div className="mb-6 text-center">
        <Link to="/kids" className="font-display font-semibold text-2xl tracking-tight">
          <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
        </Link>
      </div>

      <div className="w-full max-w-lg">
        {/* Cabi mascot tip */}
        <CabiBubble mood={info.mood} theme="light" size={72} className="mb-5 px-2" text={info.tip} />

        {/* Progress steps */}
        <div className="flex items-center gap-2 mb-6 px-2">
          {[1,2].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-body font-extrabold text-sm shrink-0 transition-all duration-300 ${
                s < step  ? "bg-brand text-white" :
                s === step? "bg-brand text-white ring-4 ring-cardline" :
                            "bg-cream text-faint"
              }`}>
                {s < step ? "✓" : s}
              </div>
              {s < 2 && <div className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${s < step ? "bg-brand" : "bg-cardline"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Parent info */}
        {step === 1 && (
          <div className="bg-white rounded-sheet shadow-sheet p-8 slide-up">
            <h1 className="font-display font-semibold text-2xl text-ink mb-1">{t("kids.signup.title")}</h1>
            <p className="text-muted text-sm mb-6">{t("kids.signup.subtitle")}</p>
            <form onSubmit={handleRegister} className="space-y-4">
              <Field label={t("kids.signup.name")}    name="name"                  value={form.name}                  onChange={change} error={errors.name} />
              <Field label={t("kids.signup.email")}   name="email"    type="email"  value={form.email}                 onChange={change} error={errors.email} />

              {/* Phone with country-code selector */}
              <div>
                <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">{t("kids.signup.phone")}</label>
                <PhoneInput
                  country={"pk"}
                  value={form.phone}
                  onChange={(value) => setForm(f => ({ ...f, phone: value }))}
                  enableSearch
                  countryCodeEditable={false}
                  inputProps={{ name: "phone" }}
                  containerStyle={{ width: "100%" }}
                  inputStyle={{ width: "100%", height: "48px", borderRadius: "12px", fontSize: "15px", borderColor: errors.phone ? "#E5484D" : "#E5DED4" }}
                  buttonStyle={{ borderTopLeftRadius: "12px", borderBottomLeftRadius: "12px", borderColor: errors.phone ? "#E5484D" : "#E5DED4", background: "#fff" }}
                />
                {errors.phone && <p className="text-danger text-xs font-body font-semibold mt-1">{Array.isArray(errors.phone) ? errors.phone[0] : errors.phone}</p>}
              </div>

              <Field label={t("kids.signup.password")} name="password" type="password" value={form.password}             onChange={change} error={errors.password}
                inputProps={{ minLength: 8 }} hint="At least 8 characters" />
              <Field label={t("kids.signup.confirmPassword")}  name="password_confirmation" type="password" value={form.password_confirmation} onChange={change} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Country" name="country" value={form.country} onChange={change} optional />
                <Field label="City"    name="city"    value={form.city}    onChange={change} optional />
              </div>
              <Field label="School Name" name="school" value={form.school} onChange={change} optional hint="Optional — helps us personalise the experience" />
              <Field label="Referral Code" name="referral_code" value={form.referral_code || ""} onChange={change} optional hint="Have a friend's code? Enter it to give them 7 extra trial days!" />
              <Field label="Promo Code" name="promo_code" value={form.promo_code || ""} onChange={change} optional hint="From a partner video or coupon? Enter it for extra free days!" />
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Creating account…" : `${t("kids.signup.create")} & Start Trial 🚀`}
              </Button>
            </form>
            <p className="text-center text-[11px] text-faint font-body font-semibold mt-3">
              📧 We'll email you a 6-digit code — <b>please check your spam / junk folder</b> if it's not in your inbox within a minute.
            </p>
            <p className="text-center text-sm text-muted font-body font-semibold mt-4">
              {t("kids.signup.haveAccount")}{" "}
              <Link to="/kids/login" className="text-brand font-extrabold">{t("kids.signup.login")}</Link>
            </p>
          </div>
        )}

        {/* STEP 2: Enter the 6-digit code — verifying signs the parent in
            immediately (proving control of the email is enough, they just
            set a password a moment ago), straight into the dashboard where
            they add their first child. */}
        {step === 2 && (
          <div className="bg-white rounded-sheet shadow-sheet p-8 text-center slide-up">
            <div
              className="w-[110px] h-[110px] rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ background: "linear-gradient(150deg,#FFB47A,#EE5F2B)" }}
            >
              <CabiMascot mood="celebrating" size={70} />
            </div>
            <h1 className="font-display font-semibold text-2xl text-ink mb-2">Check your email!</h1>
            <p className="text-muted font-body font-semibold mb-6">
              We've sent a 6-digit code to <span className="text-ink font-extrabold">{form.email}</span>.
              Enter it below to verify and start your <span className="text-brand font-extrabold">14-day trial</span>.
            </p>

            <form onSubmit={verifyOtp} className="space-y-4">
              <input
                type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                placeholder="000000"
                className={`w-full text-center border rounded-xl px-4 py-3.5 text-ink font-display font-semibold text-[28px] tracking-[0.5em] focus:ring-2 focus:ring-brand/30 outline-none transition ${
                  otpError ? "border-danger bg-danger/5" : "border-[#E5DED4] focus:border-brand"
                }`}
              />
              {otpError && <p className="text-danger text-xs font-body font-semibold">{otpError}</p>}
              <Button type="submit" disabled={verifying || otp.length !== 6} className="w-full">
                {verifying ? "Verifying…" : "Verify & Continue 🚀"}
              </Button>
            </form>

            <p className="text-[11px] text-muted font-body font-semibold rounded-card px-3 py-2 mt-4 mb-3" style={{ background: "#FFF6EE", border: "1px solid #FBE2CF" }}>
              📧 Don't see it? <b>Check your spam / junk folder</b> — codes expire after 10 minutes.
            </p>
            <button onClick={resendVerification} disabled={resending}
              className="w-full text-center text-brand font-body font-extrabold text-sm hover:underline transition disabled:opacity-60">
              {resending ? "Resending…" : "Resend code"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, error, optional = false, inputProps = {}, hint }) {
  return (
    <div>
      <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">
        {label} {optional && <span className="text-faint font-semibold">(optional)</span>}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange} {...inputProps}
        className={`w-full border rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 outline-none transition ${
          error ? "border-danger bg-danger/5" : "border-[#E5DED4] focus:border-brand"
        }`}
      />
      {hint && !error && <p className="text-faint text-xs font-body font-semibold mt-1">{hint}</p>}
      {error && <p className="text-danger text-xs font-body font-semibold mt-1">{Array.isArray(error) ? error[0] : error}</p>}
    </div>
  );
}
